import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { IModel } from '../../base/annotation';
import { Metamodel } from 'app/model/base/model';
import { LinkedServiceAdapter } from 'app/model/adapters/linkedservices/linkedservice.adapter';
import { GreenplumLinkedService } from 'app/model/linkedservice/greenplum.linkedservice';
import { akvToResource, akvToModel, secretBaseToPasswordOrAKV, passwordOrAKVtoSecretBase } from 'app/model/adapters/linkedservices/azure-key-vault.util';
import { isString } from '../../../shared/utils/string.util';
import { SecureStringReplacer } from 'app/shared/utils/securestring-replacer';
import { Constants } from '../../../shared/shared-constants';
import { DbConnectionStringBuilderService } from 'app/services/db-connection-string-builder.service';
import { AzureKeyVault } from 'app/model/linkedservice/key-vault';
import { AdapterContext, AuthenticationType, ConnectionAuthKind, ITridentResource } from 'shell-models-lib';
import { CONNECTOR_VERSION20 } from 'app/services/globals';
import { driverVersions } from 'app/registries/consts/tabular-consts';
import { SSLMode } from 'app/registries/consts/postgre-consts';
import { greenplumAdditionalFieldsAllowedKeys, GreenplumAdditionalPropertiesKeyType } from 'app/registries/consts/greenplum-const';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(GreenplumLinkedService),
    asynchronous: true,
    adapts: {
        name: '*',
        properties: {
            description: '*',
            type: '*',
            version: '*',
            connectVia: '*',
            typeProperties: {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                connectionString: (_context: AdapterContext, _value: any, ls: GreenplumLinkedService) => {
                    // when AKV is used for connection string, it is already well handled. So we don't need handle it.
                    // won't cover fully by json for now
                    if (ls && !ls.fullyByJsonEditor && !ls.enableAKV) {
                        if (ls.password instanceof AzureKeyVault) {
                            return '*';
                        }
                    }
                    return 'secret';
                },
                password: 'secret',
                pwd: 'secret',
                encryptedCredential: '*',
                sslMode: '*',
                host: '*',
                port: '*',
                database: '*',
                username: '*',
                authenticationType: '*',
            }
        }
    }
})
export class GreenplumLinkedServiceAdapter extends LinkedServiceAdapter<GreenplumLinkedService> {
    protected modelConstructor(): new () => GreenplumLinkedService {
        return GreenplumLinkedService;
    }

    protected model(): IModel {
        return GreenplumLinkedService.MODEL;
    }

    protected override toAzureTypeProperties = (tridentResource: ITridentResource): Model.GreenplumLinkedServiceTypeProperties => {
        const credentialType = tridentResource.credentialDetails.credentialType;
        switch (credentialType) {
            case ConnectionAuthKind.Basic:
                const datasourceReference: Record<'kind' | 'path', string> = JSON.parse(
                    tridentResource.datasourceReference
                );
                const path = datasourceReference.path;
                const [server, database, commandTimeout, connectionTimeout] = path.split(';');
                const [host, port] = server.split(':');
                return {
                    host,
                    port,
                    database,
                    commandTimeout,
                    connectionTimeout
                };
            default:
                return undefined;
        }
    };

    protected async propertiesToResourceAsync(linkedService: GreenplumLinkedService, linkedServiceResource: Model.LinkedServiceResource): Promise<Model.LinkedServiceResource> {
        const properties = this.properties(linkedServiceResource) as Model.GreenplumLinkedService;
        properties.type = Metamodel.modelFor(GreenplumLinkedService).name;

        if (linkedService.version === driverVersions.v2) {
            properties.version = CONNECTOR_VERSION20;
            properties.typeProperties = {
                authenticationType: AuthenticationType.Basic,
                host: linkedService.host,
                port: linkedService.port,
                database: linkedService.databaseName,
                username: linkedService.userName,
                sslMode: linkedService.sslMode != null ? linkedService.sslMode : SSLMode.Require,
                pwd: passwordOrAKVtoSecretBase(linkedService.password, this.context),
            };

            linkedService.additionalFields?.forEach((pair) => {
                if (pair.value != null) {
                    properties.typeProperties[pair.key as GreenplumAdditionalPropertiesKeyType] = pair.value;
                }
            });
            return linkedServiceResource;
        } else {
            delete properties.version;
        }

        let connectionString: string | Model.SecretBase;
        if (!linkedService.enableAKV) {
            const builder = new DbConnectionStringBuilderService(true);
            builder.add('HOST', linkedService.host || '');
            builder.add('Port', linkedService.port ? linkedService.port.toString() : '');
            builder.add('DB', linkedService.databaseName || '');
            builder.add('UID', linkedService.userName || '');
            if (linkedService.password && !(linkedService.password instanceof AzureKeyVault)) {
                builder.add('PWD', linkedService.password);
            }
            linkedService.additionalFields?.forEach((pair) => {
                builder.add(pair.key, pair.value || '');
            });
            connectionString = linkedService.host === Constants.maskedCredential &&
                linkedService.port === undefined &&
                linkedService.databaseName === Constants.maskedCredential &&
                linkedService.userName === Constants.maskedCredential &&
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                linkedService.password === Constants.maskedCredential ? {
                    type: 'SecureString',
                    value: Constants.maskedCredential
                } as Model.SecureString : this._connectionStringToResource(builder.connectionString);
        } else {
            connectionString = akvToResource(linkedService.referedAKV, this.context);
        }

        properties.typeProperties = {
            connectionString,
            password: !linkedService.enableAKV && linkedService.password instanceof AzureKeyVault ? akvToResource(linkedService.password, this.context) : undefined
        };
        return linkedServiceResource;
    }

    protected async propertiesToModelAsync(linkedServiceResource: Model.LinkedServiceResource, linkedService: GreenplumLinkedService): Promise<GreenplumLinkedService> {
        const properties = linkedServiceResource.properties as Model.GreenplumLinkedService;
        const typeProperties = properties.typeProperties;

        linkedService.host = undefined;
        linkedService.port = undefined;
        linkedService.databaseName = undefined;
        linkedService.userName = undefined;
        linkedService.password = undefined;

        linkedService.version = __IS_FABRIC_DPE__ ?
            driverVersions.v2 :
            (properties.version === CONNECTOR_VERSION20 ? driverVersions.v2 : driverVersions.v1);

        linkedService.isLegacyConnector = linkedService.version === driverVersions.v1;

        if (properties.version === CONNECTOR_VERSION20) {
            linkedService.host = typeProperties.host;
            linkedService.databaseName = typeProperties.database;
            linkedService.port = typeProperties.port;
            linkedService.userName = typeProperties.username;
            linkedService.sslMode = typeProperties.sslMode;
            linkedService.password = secretBaseToPasswordOrAKV(typeProperties.pwd, true, this.context);

            linkedService.additionalFields = [];
            for (const propertyName in typeProperties) {
                if (greenplumAdditionalFieldsAllowedKeys.includes(propertyName as GreenplumAdditionalPropertiesKeyType)) {
                    linkedService.additionalFields.push({
                        key: propertyName,
                        value: typeProperties[propertyName as keyof Model.GreenplumLinkedServiceTypeProperties]
                    });
                }
            }
        } else if (isString(typeProperties.connectionString)) {
            const builder = new DbConnectionStringBuilderService();
            builder.parse(properties.typeProperties.connectionString as string);

            linkedService.host = builder.getValue('HOST', false) || '';
            linkedService.port = parseInt(builder.getValue('Port', false), 10) || undefined;
            linkedService.databaseName = builder.getValue('DB', false) || '';
            linkedService.userName = builder.getValue('UID', false) || '';
            linkedService.password = Constants.maskedCredential;
            linkedService.additionalFields = builder.getRemainedKeyValuePairs(['HOST', 'Port', 'DB', 'UID', 'PWD', 'Password']);
            if (properties.typeProperties.password) {
                linkedService.password = secretBaseToPasswordOrAKV(properties.typeProperties.password, false, this.context);
            }
        } else if (SecureStringReplacer.isSecureStringObject(properties.typeProperties.connectionString)) {
            linkedService.host = Constants.maskedCredential;
            linkedService.databaseName = Constants.maskedCredential;
            linkedService.userName = Constants.maskedCredential;
            linkedService.password = Constants.maskedCredential;
            linkedService.port = undefined;
            if (properties.typeProperties.password) {
                linkedService.password = secretBaseToPasswordOrAKV(properties.typeProperties.password, false, this.context);
            }
        } else if (properties.typeProperties.connectionString
            && (properties.typeProperties.connectionString as Model.SecretBase).type === 'AzureKeyVaultSecret') {
            linkedService.referedAKV = akvToModel(properties.typeProperties.connectionString as Model.AzureKeyVaultSecretReference, this.context);
            linkedService.enableAKV = true;
        }
        return linkedService;
    }
}
